import { useState, useEffect } from "react";
import { BookOpen, LogOut, Trash2, RefreshCw, ArrowLeft, Pencil, Check as CheckIcon, X as XIcon } from "lucide-react";
import axios from "axios";
import { API } from "../utils/constants";

const RED = "#E82529";

// A trip in the making (backend core/drafts): where they left it, and the versions they can bring back.
function DraftRow({ draft, sessionToken, onOpen, onRemoved }) {
  const [showVersions, setShowVersions] = useState(false);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${sessionToken}` };
  const open = async () => {
    setBusy(true);
    try { onOpen((await axios.get(`${API}/drafts/${draft.draft_id}`, { headers })).data); }
    catch { alert("We couldn't open that trip — check your connection and try again."); }
    setBusy(false);
  };
  const bringBack = async (index) => {
    setBusy(true);
    try { onOpen((await axios.post(`${API}/drafts/${draft.draft_id}/restore/${index}`, {}, { headers })).data); }
    catch { alert("We couldn't bring that version back — please try again."); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Remove this trip in the making?")) return;
    try { await axios.delete(`${API}/drafts/${draft.draft_id}`, { headers }); onRemoved(draft.draft_id); }
    catch { alert("Couldn't remove it — please try again."); }
  };
  const versions = draft.versions || [];
  return (
    <div style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "16px 20px" }} data-testid={`draft-${draft.draft_id}`}>
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={open}>
          <h3 className="truncate" style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: "var(--zen-text)" }}>{draft.title}</h3>
          <p className="font-mono-label mt-1.5" style={{ fontSize: 9, letterSpacing: "0.06em", color: "var(--zen-dim)", textTransform: "none" }}>
            Last worked on {new Date(draft.updated_at).toLocaleDateString()}{draft.built?.length ? ` · ${draft.built.length} trip${draft.built.length > 1 ? "s" : ""} built from it` : ""}
          </p>
        </div>
        <button onClick={open} disabled={busy} className="zen-btn zen-btn-primary px-4 py-2 text-sm shrink-0">{draft.built?.length ? "Change the plan" : "Continue"}</button>
        <button onClick={remove} className="shrink-0 hover:opacity-70" title="Remove" style={{ color: "var(--zen-dim)", background: "none", border: "none", cursor: "pointer" }}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {versions.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowVersions(v => !v)} className="text-xs" style={{ color: "var(--zen-sub)", background: "none", border: 0, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            {showVersions ? "Hide earlier versions" : `Earlier versions (${versions.length})`}
          </button>
          {showVersions && (
            <div className="space-y-1.5" style={{ marginTop: 8 }}>
              {versions.map((v, i) => ({ v, i })).reverse().map(({ v, i }) => (
                <div key={i} className="flex items-center justify-between text-xs" style={{ color: "var(--zen-sub)" }}>
                  <span>{v.title} · {new Date(v.at).toLocaleString()}</span>
                  <button onClick={() => bringBack(i)} disabled={busy} style={{ color: "var(--zen-accent)", background: "none", border: 0, cursor: "pointer", fontWeight: 600 }}>Bring back</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MyTripsPage = ({ userEmail, sessionToken, onLoadTrip, onBack, onLogout, onOpenDraft = () => {} }) => {
  const [drafts, setDrafts] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState("");

  const startRename = (trip) => { setRenamingId(trip.save_id); setRenameText(trip.title || ""); };

  const saveRename = async (saveId) => {
    const title = renameText.trim();
    if (!title) return;
    // Optimistic — the row renames instantly; revert on failure.
    const prev = trips;
    setTrips(t => t.map(x => x.save_id === saveId ? { ...x, title } : x));
    setRenamingId(null);
    try {
      await axios.patch(`${API}/itineraries/${saveId}`, { title },
        { headers: { Authorization: `Bearer ${sessionToken}` } });
    } catch (e) {
      setTrips(prev);
      alert("Couldn't rename that trip — please try again.");
    }
  };

  useEffect(() => { loadTrips(); }, []);

  const loadTrips = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/itineraries`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      setTrips(res.data.itineraries || []);
      // trips in the making: a failure here never hides the built trips
      axios.get(`${API}/drafts`, { headers: { Authorization: `Bearer ${sessionToken}` } })
        .then(r => setDrafts(r.data.drafts || [])).catch(() => setDrafts([]));
    } catch (e) {
      // Don't tell a user with saved trips that they have none — a 401
      // (expired session) or a down API is not an empty account.
      console.error("Failed to load trips", e);
      setLoadError(true);
    }
    setLoading(false);
  };

  const deleteTrip = async (saveId) => {
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/itineraries/${saveId}`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      setTrips(prev => prev.filter(t => t.save_id !== saveId));
    } catch (e) {
      console.error("Failed to delete", e);
      setLoadError(false);
      alert("Couldn't delete that trip — please try again.");
    }
  };

  const loadTrip = async (saveId) => {
    try {
      const res = await axios.get(`${API}/itineraries/${saveId}`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      onLoadTrip(res.data.itinerary_data);
    } catch (e) { alert("We couldn't open that trip — check your connection and try again."); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--zen-bg)" }}>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <button onClick={onBack} className="font-mono-label flex items-center gap-1.5 hover:opacity-70 transition-opacity"
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, letterSpacing: "0.16em", color: "var(--zen-sub)", marginBottom: 28 }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        <div className="flex items-center justify-between mb-1">
          <h1 style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: "clamp(28px, 4vw, 36px)", color: "var(--zen-text)" }}>My Trips</h1>
          <button onClick={onLogout} className="flex items-center gap-1.5 text-sm hover:opacity-70 transition-opacity" style={{ color: "var(--zen-dim)", background: "none", border: "none", cursor: "pointer" }} data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
        <p className="text-sm mb-8" style={{ color: "var(--zen-sub)" }}>{userEmail}</p>

        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--zen-dim)" }} /></div>
        ) : loadError ? (
          <div className="text-center py-16">
            <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>We couldn't load your trips. Your session may have expired.</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={loadTrips} className="zen-btn zen-btn-outline px-5 py-2.5 text-sm">Try again</button>
              <button onClick={onLogout} className="zen-btn zen-btn-primary px-5 py-2.5 text-sm">Sign in again</button>
            </div>
          </div>
        ) : (
          <>
          {drafts.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <span className="font-mono-label" style={{ fontSize: 9, letterSpacing: "0.18em", color: "var(--zen-dim)", display: "block", marginBottom: 10 }}>IN THE MAKING</span>
              <div className="space-y-3" data-testid="drafts-list">
                {drafts.map(d => <DraftRow key={d.draft_id} draft={d} sessionToken={sessionToken} onOpen={onOpenDraft}
                  onRemoved={id => setDrafts(list => list.filter(x => x.draft_id !== id))} />)}
              </div>
              {trips.length > 0 && <span className="font-mono-label" style={{ fontSize: 9, letterSpacing: "0.18em", color: "var(--zen-dim)", display: "block", margin: "28px 0 10px" }}>YOUR TRIPS</span>}
            </div>
          )}
          {trips.length === 0 ? (drafts.length > 0 ? null : (
          <div className="text-center py-16">
            <BookOpen className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--zen-border-h)" }} />
            <p className="text-sm" style={{ color: "var(--zen-dim)" }}>No saved trips yet. Plan one and save it!</p>
          </div>
        )) : (
          <div className="space-y-3" data-testid="trips-list">
            {trips.map((trip) => (
              <div key={trip.save_id} className="flex items-center gap-4 group transition-all"
                style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "18px 22px" }}>
                {renamingId === trip.save_id ? (
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <input autoFocus value={renameText} onChange={e => setRenameText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") saveRename(trip.save_id); if (e.key === "Escape") setRenamingId(null); }}
                      className="jf-field flex-1 min-w-0" maxLength={120}
                      data-testid={`rename-input-${trip.save_id}`} />
                    <button onClick={() => saveRename(trip.save_id)} className="shrink-0 hover:opacity-70"
                      style={{ color: "var(--zen-accent)", background: "none", border: "none", cursor: "pointer" }}
                      data-testid={`rename-save-${trip.save_id}`}><CheckIcon className="w-4 h-4" /></button>
                    <button onClick={() => setRenamingId(null)} className="shrink-0 hover:opacity-70"
                      style={{ color: "var(--zen-dim)", background: "none", border: "none", cursor: "pointer" }}><XIcon className="w-4 h-4" /></button>
                  </div>
                ) : (
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadTrip(trip.save_id)} data-testid={`trip-item-${trip.save_id}`}>
                  <h3 className="truncate" style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: "var(--zen-text)" }}>{trip.title}</h3>
                  <p className="text-[13px] mt-1" style={{ color: "var(--zen-sub)" }}>
                    {trip.cities?.join(" → ")} · {trip.total_days} days
                    {trip.start_date && ` · ${trip.start_date}`}
                  </p>
                  <p className="font-mono-label mt-1.5" style={{ fontSize: 9, letterSpacing: "0.06em", color: "var(--zen-dim)", textTransform: "none" }}>
                    Saved {new Date(trip.saved_at).toLocaleDateString()}
                  </p>
                </div>
                )}
                {renamingId !== trip.save_id && (
                  <button onClick={() => startRename(trip)}
                    className="shrink-0 inline-flex items-center justify-center hover:opacity-70 transition-opacity"
                    style={{ width: 38, height: 38, borderRadius: "50%", border: "1px solid var(--zen-border-h)", background: "transparent", color: "var(--zen-dim)", cursor: "pointer" }}
                    title="Rename" data-testid={`rename-trip-${trip.save_id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => setConfirmDelete(trip.save_id)}
                  className="shrink-0 inline-flex items-center justify-center hover:opacity-70 transition-opacity"
                  style={{ width: 38, height: 38, borderRadius: "50%", border: "1px solid var(--zen-border-h)", background: "transparent", color: "var(--zen-dim)", cursor: "pointer" }}
                  data-testid={`delete-trip-${trip.save_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
          </>
        )}

      </div>

      {/* Delete confirmation — a stray tap must not destroy a saved trip */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setConfirmDelete(null)}>
          <div className="w-full max-w-xs p-6" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 8 }} onClick={e => e.stopPropagation()}>
            <h3 className="mb-2" style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: 17, color: "var(--zen-text)" }}>Delete this trip?</h3>
            <p className="text-sm mb-5" style={{ color: "var(--zen-sub)" }}>This can't be undone.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 text-sm" style={{ border: "1px solid var(--zen-border)", borderRadius: 2, color: "var(--zen-sub)", background: "transparent", cursor: "pointer" }}>Keep it</button>
              <button onClick={() => deleteTrip(confirmDelete)} className="flex-1 py-2.5 text-sm" style={{ border: "none", borderRadius: 2, color: "#fff", background: "var(--cat-nightlife-solid)", cursor: "pointer" }} data-testid="confirm-delete-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTripsPage;
